import {
  APARTMENT_CELL_FIELDS,
  APARTMENT_PERSONAL_FIELDS,
  type SpaceSlotKind,
  type RestrictedAccess,
  isPersonalField,
  mayAccessField,
  slotKey,
} from '@sivan/shared';
import { type SyntheticEvent, useState } from 'react';
import { Modal } from './Modal';
import {
  type SheetRow,
  type Structures,
  displayValue,
  kindOf,
  lockedReason,
  usedSlots,
} from './apartment-draft';
import type { PastePermissions } from './apartment-paste';
import { apartmentColumnLabel, apartmentErrorText } from './labels';

export interface FormChange {
  readonly field: string;
  /** '' on a stored value is an explicit clear. */
  readonly value: string;
}

interface ApartmentFormProps {
  readonly row: SheetRow;
  readonly structures: Structures;
  readonly permissions: PastePermissions;
  /** The restricted fields the reader may see: contacts (DR-185) and the note (DR-187). */
  readonly restricted: RestrictedAccess;
  readonly onApply: (key: string, changes: readonly FormChange[]) => void;
  readonly onClose: () => void;
}

const KINDS: readonly SpaceSlotKind[] = ['parking', 'storage'];
const NAMES = { parking: 'חניה', storage: 'מחסן' } as const;

/**
 * Mobile editing of one apartment into the same draft as the grid: its own fields and every
 * parking and storage slot, with "+ הוסף" for another. Emptying a stored value, or ticking
 * "סיום הצמדה", is an explicit clear; the review shows it before anything is saved.
 */
export function ApartmentForm({
  row,
  permissions,
  restricted,
  onApply,
  onClose,
}: ApartmentFormProps) {
  const [slots, setSlots] = useState(() => ({
    parking: Math.max(1, usedSlots(row, 'parking')),
    storage: Math.max(1, usedSlots(row, 'storage')),
  }));
  const fields = [
    ...APARTMENT_CELL_FIELDS,
    ...APARTMENT_PERSONAL_FIELDS.filter((f) => mayAccessField(f, restricted)),
    ...KINDS.flatMap((kind) =>
      Array.from({ length: slots[kind] }, (_, slot) => [
        slotKey(kind, 'number', slot),
        slotKey(kind, 'location', slot),
      ]).flat(),
    ),
  ];
  const initial = Object.fromEntries(fields.map((f) => [f, displayValue(row, f)]));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [ends, setEnds] = useState<ReadonlySet<string>>(new Set());
  const editable = (field: string) =>
    lockedReason(row, field) === null &&
    (isPersonalField(field)
      ? mayAccessField(field, permissions)
      : kindOf(field) !== undefined
        ? permissions.allocate
        : row.unitId === null
          ? permissions.create
          : permissions.edit);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const changes: FormChange[] = fields
      .filter((f) => editable(f) && (values[f] ?? '') !== (initial[f] ?? '') && !ends.has(f))
      .map((field) => ({ field, value: values[field] ?? '' }));
    for (const field of ends) changes.push({ field, value: '' });
    onApply(row.key, changes);
  };

  const title =
    row.unitId === null
      ? 'דירה חדשה'
      : `עריכת דירה ${displayValue(row, 'apartmentNumber')} בבנין ${displayValue(row, 'building')}`;
  return (
    <Modal title={title} open onClose={onClose}>
      <form className="unit-form" onSubmit={submit}>
        {fields.map((field) => {
          const id = `apartment-${field}`;
          const error = row.errors[field];
          return (
            <div key={field} className="field">
              <label htmlFor={id}>{apartmentColumnLabel(field)}</label>
              <input
                id={id}
                value={values[field] ?? ''}
                disabled={!editable(field) || ends.has(field)}
                aria-invalid={error !== undefined}
                aria-describedby={error === undefined ? undefined : `${id}-error`}
                onChange={(event) => {
                  setValues({ ...values, [field]: event.target.value });
                }}
              />
              {error !== undefined && (
                <p id={`${id}-error`} className="field__error">
                  {apartmentErrorText(field, error)}
                </p>
              )}
              {kindOf(field) !== undefined &&
                field.includes('Number') &&
                row.unitId !== null &&
                (initial[field] ?? '') !== '' &&
                editable(field) && (
                  <label className="toolbar__toggle">
                    <input
                      type="checkbox"
                      checked={ends.has(field)}
                      onChange={(event) => {
                        const next = new Set(ends);
                        if (event.target.checked) next.add(field);
                        else next.delete(field);
                        setEnds(next);
                      }}
                    />
                    <span>סיום ההצמדה</span>
                  </label>
                )}
            </div>
          );
        })}
        {permissions.allocate && (
          <div className="pair-actions">
            {KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="button button--small"
                onClick={() => {
                  setSlots({ ...slots, [kind]: slots[kind] + 1 });
                }}
              >
                + הוסף {NAMES[kind]}
              </button>
            ))}
          </div>
        )}
        {restricted.contact !== true && <p className="hint">פרטי הדיירים אינם זמינים לך.</p>}
        <footer className="modal__footer">
          <button type="submit" className="button button--primary">
            עדכון הטיוטה
          </button>
          <button type="button" className="button" onClick={onClose}>
            ביטול
          </button>
        </footer>
      </form>
    </Modal>
  );
}
